import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Layout } from '@/components/Layout';
import { Users, FileText, Settings, Shield, UserPlus, Upload, Plus, CheckCircle2, Info, Mail, User } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

type StatWithValue = {
  title: string;
  value: string;
  description: string;
  icon: LucideIcon;
  color: string;
  isZero?: boolean;
  zeroStateMessage?: string;
};

type StatWithBadge = {
  title: string;
  value: null;
  description: string;
  icon: LucideIcon;
  color: string;
  statusBadge: { label: string; variant: 'success' | 'info' };
};

type DashboardStat = StatWithValue | StatWithBadge;

export function DashboardPage() {
  const { user } = useAuth();

  const stats: DashboardStat[] = [
    {
      title: 'Total Users',
      value: '0',
      description: 'Active users in the system',
      icon: Users,
      color: 'text-primary',
      isZero: true,
      zeroStateMessage: 'No users yet. Add your first user to get started.',
    },
    {
      title: 'Documents',
      value: '0',
      description: 'Total documents stored',
      icon: FileText,
      color: 'text-primary',
      isZero: true,
      zeroStateMessage: 'No documents uploaded. Start by uploading your first document.',
    },
    {
      title: 'Settings',
      value: null,
      statusBadge: { label: 'Configured', variant: 'success' as const },
      description: 'System configuration',
      icon: Settings,
      color: 'text-primary',
    },
    {
      title: 'Admin Access',
      value: null,
      statusBadge: { label: 'Enabled', variant: 'success' as const },
      description: 'Full administrative privileges granted',
      icon: Shield,
      color: 'text-primary',
    },
  ];

  return (
    <Layout>
        {/* Stats Grid */}
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 mb-6 md:mb-8">
          {stats.map((stat) => {
            const Icon = stat.icon;
            return (
              <Card key={stat.title}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">
                    {stat.title}
                  </CardTitle>
                  <Icon className={`h-4 w-4 ${stat.color}`} />
                </CardHeader>
                <CardContent>
                  {stat.value !== null ? (
                    <>
                      <div className="text-2xl font-bold">{stat.value}</div>
                      {stat.isZero && stat.zeroStateMessage && (
                        <div className="mt-2 p-2 bg-muted/50 rounded-md border border-muted">
                          <div className="flex items-start gap-2">
                            <Info className="h-3 w-3 text-muted-foreground mt-0.5 flex-shrink-0" />
                            <p className="text-xs text-muted-foreground leading-relaxed">
                              {stat.zeroStateMessage}
                            </p>
                          </div>
                        </div>
                      )}
                      {!stat.isZero && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {stat.description}
                        </p>
                      )}
                    </>
                  ) : stat.statusBadge ? (
                    <>
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                          stat.statusBadge.variant === 'success'
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                            : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                        }`}>
                          <CheckCircle2 className="h-3 w-3" />
                          {stat.statusBadge.label}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {stat.description}
                      </p>
                    </>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      {'description' in stat ? stat.description : ''}
                    </p>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Main Dashboard Content */}
        <div className="grid gap-4 grid-cols-1 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
              <CardDescription>
                Common administrative tasks
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button variant="outline" className="w-full justify-start">
                <UserPlus className="mr-2 h-4 w-4" />
                Add New User
              </Button>
              <Button variant="outline" className="w-full justify-start">
                <Upload className="mr-2 h-4 w-4" />
                Upload Document
              </Button>
              <Button variant="outline" className="w-full justify-start">
                <Plus className="mr-2 h-4 w-4" />
                Configure System
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>System Information</CardTitle>
              <CardDescription>
                Current system status and details
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 text-sm">
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Mail className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-muted-foreground mb-1">Admin Email</p>
                    <p className="font-medium break-all">{user?.email || 'Not available'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <User className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-muted-foreground mb-1">Role</p>
                    <div className="flex items-center gap-2">
                      <span className="font-medium capitalize">{user?.role || 'Not available'}</span>
                      {user?.role && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                          <Shield className="h-3 w-3" />
                          Admin
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
    </Layout>
  );
}

